import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { DGELicenseForm } from '@/components/forms/LicenseForm'
import {
  ActionFormItem,
  ActionsMenu,
  ActionToggleOperatorLicenseDeactivateItem
} from '@/components/table/actions'
import { FilterableTable } from '@/components/table/base'
import { STORE_STATUS } from '@/constants/store'
import { streamingRestApi } from '@/service/streamingApi'
import { useOperatorLicensesStoreDGE } from '@/stores/operator/licenses.dge.store'

import {
  DGE_TABLE_COLUMNS,
  getDGETableFilters,
  getLicenseTableRows,
  LICENSE_STATUSES,
  LicenseRow
} from './licenses.dge.table'

export const DGE_OPERATOR_LICENSES_TABLE_ID = 'dge-operator-licenses-table'

export const LicensesViewDGE = () => {
  const operatorIdParam = useParams()['operatorId']
  const operatorId = operatorIdParam ? Number(operatorIdParam) : null

  const { data, fetchData, resetState, isFetching, isLoading } = useOperatorLicensesStoreDGE(
    useShallow((state) => ({
      data: state.data,
      fetchData: state.fetchData,
      resetState: state.resetState,
      isLoading: state.status === STORE_STATUS.loading,
      isFetching: state.status === STORE_STATUS.fetching
    }))
  )

  useEffect(() => {
    if (operatorId) {
      fetchData({ operatorId })
    }
    return () => {
      resetState()
    }
  }, [operatorId])

  const tableRows = useMemo(() => getLicenseTableRows(data), [data])
  const tableFilters = useMemo(() => getDGETableFilters(tableRows), [tableRows])

  const handleOperatorLicenseDeactivationToggle = async (row: LicenseRow) => {
    if (!operatorId) return
    if (row.status.type === LICENSE_STATUSES.DEACTIVATED) {
      await streamingRestApi.endpoints.operators.enableLicense(row.id)
    } else {
      await streamingRestApi.endpoints.operators.deleteLicense(row.id)
    }
    await fetchData({ operatorId: operatorId })
  }

  if (!operatorId) return null

  return (
    <FilterableTable
      tableId={DGE_OPERATOR_LICENSES_TABLE_ID}
      columns={DGE_TABLE_COLUMNS}
      filters={tableFilters}
      data={tableRows}
      loading={isLoading}
      disableColumnVisibility
      fetching={isFetching}
      clientSideFilters
      defaultSort={{
        attribute: 'year',
        order: 'desc'
      }}
      clientSideSortAndPagination
      withCheckboxes
      rowActions={
        <ActionsMenu>
          <ActionFormItem
            getForm={(row: LicenseRow) => (
              <DGELicenseForm
                defaultValues={row}
                onSubmit={() => fetchData({ operatorId })}
                data={{ operatorId, operatorLicenses: data }}
              />
            )}
          />
          <ActionToggleOperatorLicenseDeactivateItem
            displaySeparately
            onClick={handleOperatorLicenseDeactivationToggle}
          />
        </ActionsMenu>
      }
    />
  )
}
